import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UserHasPermission } from 'utils/auth/roles-and-permissions';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { useGetSuppliersList } from '@/modules/workforce/Contracts/apiHooks';
import { IContracts } from '@/modules/workforce/Contracts/types';
import { AddEditCrew } from '@/modules/workforce/Crew/components/Modals/AddEditCrew';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import { permissions } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const CrewSelection = () => {
  const { t } = useTranslation('common');
  const { watch, setValue } = useFormContext<IContracts.IAddContractSchema>();

  const { openModal, closeModal } = useModal();

  const { user } = useAuth();

  const entityWatcher = watch('entity');
  const supplierWatcher = watch('supplier');

  const { data, fetchNextPage, refetch } = useGetSuppliersList({
    entityId: entityWatcher.id,
    pageNumber: 1,
    pageSize: 10,
  });

  const crewsList = data?.pages?.flatMap((page) => page?.items || []) || [];
  const formattedCrewsList = crewsList.map((crew) => ({
    ...crew,
    isDisabled: false,
  }));

  const hasPermissionToCreateCrew = UserHasPermission({
    role: user?.userGroupId,
    action: permissions.CAN_ADD_CREW,
  });

  const infiniteSelectFieldGetValue = useCallback(
    (item: IContracts.ISupplierSchema) => ({
      name: item?.supplierName,
      id: item?.supplierId,
    }),
    []
  );

  const handleCrewAdded = async () => {
    const { data } = await refetch();

    const crewsList = data?.pages?.flatMap((page) => page.items) || [];
    const formattedCrewsList = crewsList.map((crew) => ({
      ...crew,
      isDisabled: false,
    }));

    const lastCrew = formattedCrewsList[formattedCrewsList.length - 1];

    if (lastCrew) {
      setValue('supplier', infiniteSelectFieldGetValue(lastCrew));
    }
  };

  // TODO: fix calling instant before
  return (
    <InfiniteSelectField<IContracts.ISupplierSchema, false>
      id='supplier'
      isSearchable
      width='w-full'
      py='0.5rem'
      px='0.5rem'
      borderRadius='1rem'
      fetchNextPage={fetchNextPage}
      label={t('Crew')}
      placeholder={t('search-crew')}
      list={formattedCrewsList}
      value={formattedCrewsList.find(
        (item) => item?.supplierId === supplierWatcher?.id
      )}
      createOption={
        hasPermissionToCreateCrew
          ? {
              title: t('add-crew'),
              subTitle: t('add-crew-subtitle'),
              onClick: () =>
                openModal(<AddEditCrew onSuccess={handleCrewAdded} />, {
                  variant: 'full',
                  title: t('add-crew'),
                  onClose: async () => {
                    closeModal();
                  },
                }),
            }
          : undefined
      }
      borderColor={themeColors().grey[200]}
      getLabel={(item: IContracts.ISupplierSchema) => item?.supplierName}
      getValue={infiniteSelectFieldGetValue}
      options={[
        {
          label: 'Crews',
          options: formattedCrewsList,
        },
      ]}
    />
  );
};

export default CrewSelection;
